import React, { useEffect, useState, useRef } from 'react';
import { ImageOff } from 'lucide-react';
import { resolveTauriMediaUrl, getCachedTauriMediaUrl } from './fetch-wrapper';

type TauriImageProps = Omit<
  React.ImgHTMLAttributes<HTMLImageElement>,
  'src'
> & {
  src: string | ArrayBuffer | Uint8Array;
  alt?: string;
  className?: string;
};

// Check if we're running in Tauri environment
const isTauriEnv = typeof window !== 'undefined' && '__TAURI__' in window;

export const TauriImage: React.FC<TauriImageProps> = ({
  src,
  alt,
  className,
  ...props
}) => {
  // Seed initial state from the module-level resolved-URL cache so that a
  // thumbnail we've already resolved renders synchronously on mount — no
  // skeleton flash when navigating back to the bookshelf.
  const initialCachedUrl =
    isTauriEnv && typeof src === 'string' ? getCachedTauriMediaUrl(src) : null;
  const [blobUrl, setBlobUrl] = useState<string | null>(initialCachedUrl);
  const [isLoading, setIsLoading] = useState(initialCachedUrl === null);
  const [hasError, setHasError] = useState(false);
  const previousSrcRef = useRef<string | ArrayBuffer | Uint8Array | null>(null);

  useEffect(() => {
    let createdUrl: string | null = null;
    let mounted = true;

    // If this src was resolved before, the initial state already points at
    // the cached URL. Skip the async loadImage entirely to avoid flashing
    // the skeleton via setIsLoading(true) on a re-mount.
    if (typeof src === 'string' && getCachedTauriMediaUrl(src)) {
      setIsLoading(false);
      setHasError(false);
      return () => {
        mounted = false;
      };
    }

    const loadImage = async () => {
      setIsLoading(true);
      setHasError(false);

      try {
        // If src is binary, convert directly to a blob URL
        if (src instanceof ArrayBuffer || src instanceof Uint8Array) {
          const blob = new Blob([
            src instanceof Uint8Array ? src : new Uint8Array(src),
          ]);
          createdUrl = URL.createObjectURL(blob);
          if (!mounted) {
            // Component unmounted, revoke immediately and exit
            URL.revokeObjectURL(createdUrl);
            createdUrl = null;
            return;
          }
          setBlobUrl(createdUrl);
          return;
        }

        // Otherwise src is a string URL; resolve to disk URL or ephemeral blob
        const getMimeType = (url: string) => {
          // Strip query params and hash before extracting extension
          const cleanUrl = url.split(/[?#]/)[0];
          const extension = cleanUrl.split('.').pop()?.toLowerCase();
          switch (extension) {
            case 'jpg':
            case 'jpeg':
              return 'image/jpeg';
            case 'png':
              return 'image/png';
            case 'gif':
              return 'image/gif';
            case 'webp':
              return 'image/webp';
            case 'svg':
              return 'image/svg+xml';
            default:
              return 'image/jpeg'; // fallback
          }
        };
        const mimeType = getMimeType(src as string);
        const url = await resolveTauriMediaUrl(src as string, mimeType);
        // Track blob URL immediately for cleanup
        if (url.startsWith('blob:')) {
          createdUrl = url;
        }
        if (!mounted) {
          // Component unmounted, revoke blob URL if created and exit
          if (createdUrl) {
            URL.revokeObjectURL(createdUrl);
            createdUrl = null;
          }
          return;
        }
        setBlobUrl(url);
      } catch (error) {
        console.error('Error loading image:', error);
        if (!mounted) return;
        setHasError(true);
        // Fallback to original src if tauriFetch fails
        if (typeof src === 'string') {
          setBlobUrl(src);
        } else {
          try {
            const blob = new Blob([
              src instanceof Uint8Array ? src : new Uint8Array(src),
            ]);
            createdUrl = URL.createObjectURL(blob);
            if (!mounted) {
              URL.revokeObjectURL(createdUrl);
              createdUrl = null;
              return;
            }
            setBlobUrl(createdUrl);
          } catch {}
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    if (isTauriEnv) {
      loadImage();
    } else {
      // In web environment, use string URL as-is, or turn binary into blob URL
      if (typeof src === 'string') {
        setBlobUrl(src);
      } else {
        try {
          const blob = new Blob([
            src instanceof Uint8Array ? src : new Uint8Array(src),
          ]);
          createdUrl = URL.createObjectURL(blob);
          setBlobUrl(createdUrl);
        } catch {
          setHasError(true);
        }
      }
      setIsLoading(false);
    }

    return () => {
      mounted = false;
      // If we produced a blob: URL, revoke it using the local createdUrl
      if (createdUrl && createdUrl.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(createdUrl);
        } catch {}
      }
    };
  }, [src]);

  // Update previous src reference
  useEffect(() => {
    previousSrcRef.current = src;
  }, [src]);

  // Show skeleton while loading in Tauri environment
  if (isTauriEnv && isLoading) {
    return (
      <div
        className={`rc-animate-pulse rc-bg-gray-200 rc-rounded ${
          className || ''
        }`}
        style={{
          width: props.width || '100%',
          height: props.height || '200px',
          minHeight: props.height || '200px',
        }}
      />
    );
  }

  // Show error state in Tauri environment
  if (isTauriEnv && hasError) {
    return (
      <div
        className={`rc-bg-gray-100 rc-border-2 rc-border-dashed rc-border-gray-300 rc-rounded rc-flex rc-items-center rc-justify-center ${
          className || ''
        }`}
        style={{
          width: props.width || '100%',
          height: props.height || '200px',
          minHeight: props.height || '200px',
        }}
      >
        <div className="rc-text-center rc-text-gray-500">
          <ImageOff className="rc-w-8 rc-h-8 rc-mx-auto rc-mb-2" />
          <p className="rc-text-sm">Failed to load image</p>
        </div>
      </div>
    );
  }

  // Only show image when blobUrl is available in Tauri environment
  if (isTauriEnv) {
    return (
      <img {...props} src={blobUrl || ''} alt={alt} className={className} />
    );
  }

  return (
    <img
      {...props}
      src={blobUrl || (typeof src === 'string' ? src : '')}
      alt={alt}
      className={className}
    />
  );
};
